<template>
  <div v-show="visible">
    <div class="v-popup-formation-cycle">
      <div class="v-popup__header">
        <p><span>데이터 상세 분석</span></p>
        <a @click="close">
          <div class="v-popup__close-btn" title="Close">
            <div class="v-popup__close-box"><span></span><span></span></div>
          </div>
        </a>
      </div>

      <div class="v-popup__content">
        <div class="v-popup-layout">
          <div class="v-popup-layout__left-panel" :style="{ width: leftPanelExpanded ? '250px' : '60px' }">
            <div class="v-popup-accordion-header" @click="toggleLeftPanel">
              <span>고유 ID</span>
              <span class="v-popup-accordion__arrow" :style="{ transform: leftPanelExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }">▼</span>
            </div>
            <div v-show="leftPanelExpanded" class="v-popup-left-panel__content-wrapper">
              <div class="v-popup-left-panel__search">
                <input type="text" @input="searchIds" v-model="searchKeyword" placeholder="ID 검색..."
                       class="v-popup__input">
                <label class="v-popup__checkbox-label">
                  <input type="checkbox" @change="toggleSelectAll" :checked="isAllSelected">
                  <span>전체선택</span>
                  <button class="v-popup-refresh-btn" @click="refreshIdList" title="새로고침">
                    ↺
                  </button>
                </label>
              </div>
              <div class="v-popup-left-panel__list">
                <HotTableMain ref="idListHotRef" :settings="idListHotSettings"></HotTableMain>
              </div>
              <div v-show="selectedIds.length > 0" class="v-popup-left-panel__footer">
                <a class="btn_type1" @click="applySelection" style="width: 100%; text-align: center;">
                  적용 ({{ selectedIds.length }}건)
                </a>
              </div>
            </div>
          </div>

          <div class="v-popup-layout__right-panel">
            <div class="v-popup-right-panel__top">
              <div ref="analysisScrollContainer" class="v-popup-analysis-scroll-container">
                <div class="v-popup-analysis-container" v-if="analysisColumns.length>0">
                  <div
                      v-for="column in analysisColumns"
                      :key="column.key"
                      class="v-popup-analysis-card"
                      :class="{ 'is-highlighted': selectedColumnKey === column.key }"
                      @click="selectColumn(column)"
                  >
                    <div class="v-popup-analysis-card__title">{{ column.label }}</div>
                    <div class="v-popup-analysis-card__options">
                      <div class="v-popup-analysis-controls">
                        <div class="v-popup-radio-group">
                          <label v-for="option in column.analysisOptions" :key="option.value"
                                 class="v-popup__radio-label">
                            <input type="radio" :value="option.value" v-model="columnAnalysisSelections[column.key]"
                                   :checked="option.checked">
                            {{ option.text }}
                          </label>
                        </div>
                        <a class="v-popup-chart-btn"
                           @click="openChartPopup(column, column.analysisOptions.find(o => o.value === columnAnalysisSelections[column.key]))"
                           @mouseover="showChartPreview($event, column, column.analysisOptions.find(o => o.value === columnAnalysisSelections[column.key]))"
                           @mouseleave="hideChartPreview">
                          차트 보기
                        </a>
                      </div>
                    </div>
                    <div class="v-popup-analysis-card__results">
                      <div v-for="(result, index) in currentAnalysisResults[column.key]" :key="index"
                           class="v-popup-result-item"
                           :class="`is-style-${columnAnalysisSelections[column.key]}`"
                      >
                        {{ result }}
                      </div>
                    </div>
                  </div>
                </div>
                <div v-else-if="!loading" class="nodata_txt">
                  <p>노드에 입력된 데이터가 존재하지 않습니다.</p>
                </div>
              </div>
            </div>
            <div class="v-popup-right-panel__bottom">
              <HotTableMain ref="handsomeRef" :settings="hotSettings"></HotTableMain>
            </div>
          </div>
        </div>
      </div>

      <div class="v-popup__footer">
        <a class="btn_type3" @click="downloadExcel">Excel 다운로드</a>
        <a class="btn_type2" @click="close">닫기</a>
      </div>
    </div>

    <div class="v-popup-mask" @click="close"></div>
    <Loading :visible="loading" type="fixed"/>
    <ChartPopup :visible="chartPopup.visible" :title="chartPopup.title" :chart-data="chartPopup.chartData"
                :type="chartPopup.type" @close="closeChartPopup"/>
    <div
            v-if="chartPreview.visible"
            class="v-popup-chart-preview"
            :style="{ top: `${chartPreview.position.top}px`, left: `${chartPreview.position.left}px` }"
    >
      <Bar :chart-data="chartPreview.data" :options="{ responsive: true, plugins: { legend: { display: false } } }"/>
    </div>
  </div>
</template>

<script>
  import Loading from '@common/loading.vue';
  import ChartPopup from './ChartPopup.vue';
  import {createNamespacedHelpers} from 'vuex';
  import {HotTable} from "@handsontable/vue";
  import {registerAllModules} from 'handsontable/registry';
  import {Bar, Pie, Doughnut, Line as LineChartGenerator} from 'vue-chartjs';
  import {
    Chart as ChartJS,
    Title,
    Tooltip,
    Legend,
    BarElement,
    CategoryScale,
    LinearScale,
    ArcElement,
    PointElement,
    LineElement,
    BarController,
  } from 'chart.js';
  import {settingHeader, groupBy, COL_PREFIX, DEFAULT_COL_INDEX} from "@/util/HubHandsonTable";
  import ExcelJS from 'exceljs';

  registerAllModules();
  ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale, ArcElement, PointElement, LineElement, BarController,);
  const Hub = createNamespacedHelpers('Hub');

  const licenseKey = "6c578-61cf1-e8cf5-b453c-1f850";

  export default {
    name: 'VisualizationPopup',
    components: {Loading, ChartPopup, HotTableMain: HotTable, Bar, Pie, Doughnut, LineChartGenerator},
    data() {
      return {
        // 상태 관리
        loading: false,
        visible: false,
        leftPanelExpanded: true,

        // main col
        idColumnKey: '',

        // 왼쪽 패널
        searchKeyword: '',
        selectedIds: [],
        appliedIds: [],
        idListHotInstance: null,
        originalIdData: [], // 원본 ID 데이터 보관
        idListHotSettings: {
          data: [],
          colHeaders: false,
          rowHeaders: true,
          columns: [
            {data: 'selected', type: 'checkbox', className: 'htCenter', width: 40},
            {data: 'id', title: '고유 ID', readOnly: true},
          ],
          width: '100%',
          height: '99%',
          fillHandle: false,
          licenseKey: licenseKey,
          afterChange: (changes, source) => {
            if (source === 'edit') { // Checkbox click
              this.updateSelectedIdsFromTable();
            }
          },
          afterOnCellMouseDown: (event, coords) => {
            const currentValue = this.idListHotInstance.getDataAtCell(coords.row, 0);
            this.idListHotInstance.setDataAtCell(coords.row, 0, !currentValue);
          },
        },

        // 오른쪽 패널
        selectedColumnKey: null,
        hotInstance: null,
        cellsFromDB: [],
        originalTableData: [], // 원본 테이블 데이터 보관
        hotSettings: {
          data: [],
          width: '99%',
          height: '99%',
          autoWrapRow: true,
          autoWrapCol: true,
          rowHeaders: true,
          colHeaders: false,
          readOnly: true,
          fillHandle: false, // 추가
          fixedColumnsStart: 3,
          autoRowSize: false, // row 크기 자동 조정 비활성화
          autoColumnSize: {
            useHeaders: true, // 헤더 내용을 기준으로 자동 크기 조정
          },
          rowHeights: 23, // 고정 row 높이 설정
          manualColumnResize: true,
          licenseKey: licenseKey,
          afterOnCellMouseDown: (event, coords) => {
            if (coords.row < 0 && coords.col >= 0) {
              const columnMeta = this.hotInstance.getSettings().columns[coords.col];
              if (columnMeta) {
                this.selectColumn({key: columnMeta.data, label: columnMeta.title || columnMeta.name});
              }
            }
          },
          // <<-- 요청에 따라 CSS 클래스 사용 방식으로 변경된 부분 -->>
          afterRenderer: (TD, row, col, prop) => {
            const rowSourceData = this.hotInstance.getSourceDataAtRow(row);

            // appliedIds에 포함된 행 강조
            if (rowSourceData && this.appliedIds.includes(rowSourceData[this.idColumnKey])) {
              TD.classList.add('is-highlighted');
            } else {
              TD.classList.remove('is-highlighted');
            }
            // 선택된 컬럼 강조
            if (prop === this.selectedColumnKey) {
              TD.classList.add('is-col-highlighted');
            } else {
              TD.classList.remove('is-col-highlighted');
            }
          },
          contextMenu: {
            items: {
              viewerApi: {
                name: '이미지뷰어',
                hidden: (() => {
                  const selectedRange = this.hotInstance.getSelectedRange();
                  if (!selectedRange) {
                    return true;
                  }
                  return ((selectedRange[0].from.row !== selectedRange[0].to.row || selectedRange[0].from.col !== selectedRange[0].to.col)
                      || this.hotInstance.getSettings().columns[selectedRange[0].from.col].propertyType != this.PROPERTY_TYPE.IMAGE.value)
                }),
                callback: (async (key, selection) => {
                  const {row, col} = selection[0].start;
                  const rowId = this.hotInstance.getDataAtCell(row, DEFAULT_COL_INDEX.rowId);
                  const cellColMeta = this.hotInstance.getColumnMeta(col)
                  const mappingKey = this.$route.query.processId + "_" + cellColMeta.data.replace(COL_PREFIX, "") + "_" + rowId;
                  await this.imageViewerLoad(mappingKey);
                })
              },
            }
          }
        },

        // 분석
        analysisColumns: [],
        columnAnalysisSelections: {},
        analysisFunctions: {
          frequency: this.calculateFrequency,
          statistical: this.calculateStatistical,
          range: this.calculateRange,
        },
        chartPopup: {visible: false, title: '', chartData: {labels: [], datasets: []}, type: 'bar'},
        chartPreview: {
          visible: false,
          columnKey: null,
          optionValue: null,
          position: {top: 0, left: 0},
          data: {labels: [], datasets: []},
        },
      };
    },
    computed: {
      ...Hub.mapGetters(['getVisualizationPopObj', 'getSelectedReleaseProcess']),
      isAllSelected() {
        if (!this.idListHotInstance) return false;
        const currentCheckedData = this.originalIdData.filter(c=>c.selected);
        return currentCheckedData.length > 0 && this.originalIdData.length === currentCheckedData.length;
      },
      currentAnalysisResults() {
        if (!this.visible || !this.hotInstance || this.analysisColumns.length === 0) return {};
        const sourceData = this.hotInstance.getSourceData();
        const dataToAnalyze = this.appliedIds.length > 0
            ? sourceData.filter(row => this.appliedIds.includes(row[this.idColumnKey]))
            : sourceData;

        if (dataToAnalyze.length === 0) return {};

        const results = {};
        for (const column of this.analysisColumns) {
          const analysisType = this.columnAnalysisSelections[column.key];
          const analysisFunc = this.analysisFunctions[analysisType];
          if (analysisFunc) {
            results[column.key] = analysisFunc(dataToAnalyze, column.key);
          }
        }
        return results;
      },
    },
    watch: {
      getVisualizationPopObj: {
        immediate: true,
        deep: true,
        async handler(value) {
          if (value.visualizationPopFlag) {
            this.show();
            await this.initialize();
          } else {
            this.resetState();
          }
        },
      },
    },
    methods: {
      ...Hub.mapMutations(['setVisualizationPopObj', 'setHotInstance']),
      /*######################################################################################*/
      /*세팅*/
      async initialize() {
        this.loading = true;
        try {
          await this.initializeTable();
        } catch (e) {
          console.error('초기화 중 오류 발생:', e);
        } finally {
          this.loading = false;
        }
      },
      async initializeTable() {
        // 1. 데이터 가져오기
        const experimentDataList = await this.$wApi.experiment.getNodekeyExperimentData({releaseProcessid: this.getSelectedReleaseProcess.idReleaseProcess, nodeKey: this.getVisualizationPopObj?.selectedNodeInfo?.key});
        // const experimentDataList = await this.$wApi.experiment.getExperimentData({processId: this.$route.query.processId});
        if (experimentDataList.length > 0) {
          this.cellsFromDB = experimentDataList;
        }

        // 2. 헤더 및 컬럼 설정
        const {columns, hiddenIndexes} = await settingHeader(
            this.$wApi, this.$route, this.getSelectedReleaseProcess
        );

        // 3. 헤더 처리 
        const filteredColumns = this.filterColumnsForAnalysis(columns);

        // 3. 데이터 가공
        const tableData = this.buildRowData(this.cellsFromDB, filteredColumns);
        this.originalTableData = [...tableData];

        // 4. 분석 대상 컬럼 및 기본 분석 방법 설정
        this.analysisColumns = filteredColumns
            .filter(c => c.processPropertyType !== 'HIDDEN' && c.processPropertyType !== 'NO' && c.processPropertyType !== 'SAMPLE' && c.processPropertyType !== 'DATE')
            .map(c => ({
              key: c.data,
              label: c.propertyUnit != null && c.propertyUnit !== "" ? `${c.name}[${c.propertyUnit}]` : `${c.name}`,
              dataType: c.type,
              analysisOptions: this.getAnalysisOptionsForDataType(c.type),
            }));
        this.analysisColumns.forEach(c => {
          this.$set(this.columnAnalysisSelections, c.key, c.analysisOptions[0].value);
        });

        // 6. Handsontable 인스턴스 설정 및 초기화
        this.hotSettings.colHeaders = filteredColumns.map(col => col.propertyUnit != null && col.propertyUnit !== "" ? `${col.name}[${col.propertyUnit}]` : `${col.name}`); // columns의 name 속성 사용 propertyUnit
        this.hotSettings.columns = filteredColumns;
        this.hotSettings.hiddenColumns = {columns: hiddenIndexes};
        this.hotSettings.data = tableData;

        // ID 리스트 Handsontable 설정
        this.idColumnKey = columns.find(c => c.processPropertyType === 'NO')?.data;
        if (this.idColumnKey) {
          this.idListHotSettings.columns[1].data = this.idColumnKey;
          const idListData = tableData.map(row => ({[this.idColumnKey]: row[this.idColumnKey], selected: true}));
          this.originalIdData = [...idListData];
          this.idListHotSettings.data = this.originalIdData;
          // 초기 선택된 ID 목록 설정
          this.selectedIds = this.originalIdData.map(data => data[this.idColumnKey]);
          this.appliedIds = [...this.selectedIds]; // 초기 적용도 함께
        }
        await this.$nextTick(() => {
          this.hotInstance = this.$refs.handsomeRef?.hotInstance;
          this.idListHotInstance = this.$refs.idListHotRef?.hotInstance;
          if (this.hotInstance) {
            this.setHotInstance(this.hotInstance);
          }
          if (this.analysisColumns.length > 0) {
            this.selectColumn(this.analysisColumns[0]); // 첫 번째 분석 컬럼을 기본으로 선택
          }
          this.hotInstance.updateSettings(this.hotSettings)
          this.idListHotInstance.updateSettings(this.idListHotSettings)
          this.applySelection();
        });
      },
      // 분석용 컬럼 필터링
      filterColumnsForAnalysis(columns) {
        if (!columns) return [];
        const selectedKey = this.getVisualizationPopObj?.selectedNodeInfo?.key;
        return columns.filter(column => {
          return column.key == null || column.key == selectedKey;
        });
      },
      // 데이터 타입에 따른 분석 옵션
      getAnalysisOptionsForDataType(dataType) {
        if (dataType === 'numeric') {
          return [
            {text: '통계적 비교', value: 'statistical', chartType: 'bar'},
            {text: '범위 분석', value: 'range', chartType: 'bar'},
          ];
        } else {
          return [
            {text: '빈도수 분석', value: 'frequency', chartType: 'bar'},
          ];
        }
      },
      buildRowData(cellsFromDB, columns) {
        const cellGroupByRowId = groupBy(cellsFromDB, "rowId").sort((a, b) => a[1][0].createdRowDate - b[1][0].createdRowDate);
        const handsonTableData = {};
        cellGroupByRowId.forEach(rowInfo => {
          const rowId = rowInfo[0];
          if (!handsonTableData[rowId]) {
            handsonTableData[rowId] = {};
          }
          rowInfo[1].forEach((cell, index) => {
            if (index === 0) {
              handsonTableData[rowId][`${COL_PREFIX}rowId`] = rowId;
              handsonTableData[rowId][`${COL_PREFIX}experimentId`] = cell.experimentId;
            }
            const colDefinition = columns.find(c => c.data === `${COL_PREFIX}${cell.processPropertyId}`);
            if (colDefinition) {
              handsonTableData[rowId][colDefinition.data] = cell.value;
            }
          });
        });
        // 모든 컬럼 키가 존재하는지 확인하고 없으면 빈 값으로 초기화합니다.
        const allColumnKeys = columns.map(c => c.data);
        const finalData = Object.values(handsonTableData);
        finalData.forEach(row => {
          allColumnKeys.forEach(key => {
            if (!row.hasOwnProperty(key)) {
              row[key] = '';
            }
          });
        });
        return finalData;
      },
      /*######################################################################################*/
      /*동작*/
      // 왼쪽 패널 메소드
      searchIds() {
        const searchTerm = this.searchKeyword.trim();
        let filteredData;
        if (searchTerm === '') {
          // 검색어가 없으면 원본 데이터 전체 표시
          filteredData = [...this.originalIdData];
        } else {
          // 검색어가 있으면 원본 데이터에서 필터링
          const idColumnKey = this.idListHotSettings.columns[1].data;
          filteredData = this.originalIdData.filter(row => {
            return String(row[idColumnKey]).toLowerCase().includes(searchTerm.toLowerCase());
          });
        }
        this.idListHotInstance.loadData(filteredData);
        this.updateSelectedIdsFromTable();
      },
      toggleSelectAll(event) {
        const isChecked = event.target.checked;
        const currentData = this.idListHotInstance.getData();
        currentData.forEach((row, index) => {
          this.idListHotInstance.setDataAtCell(index, 0, isChecked);
        });
        this.updateSelectedIdsFromTable();
      },
      updateSelectedIdsFromTable() {
        this.selectedIds = this.originalIdData.filter(c=>c.selected).map(row => row[this.idColumnKey]);
      },
      // 오른쪽 패널 메소드
      applySelection() {
        this.appliedIds = [...this.selectedIds];
        if (this.appliedIds.length > 0) {
          const sourceData = [...this.originalTableData];
          const selectedRows = sourceData.filter(row => this.appliedIds.includes(row[this.idColumnKey]));
          const unselectedRows = sourceData.filter(row => !this.appliedIds.includes(row[this.idColumnKey]));
          const reorderedData = [...selectedRows, ...unselectedRows];
          this.hotInstance.loadData(reorderedData);
          this.hotInstance.scrollViewportTo(0, 0);
        } else {
          this.hotInstance.loadData([...this.originalTableData]);
        }
        this.hotInstance.render();
      },
      selectColumn(column) {
        this.selectedColumnKey = column.key;
        this.scrollToAnalysisColumn(column.key);

        // 해당 컬럼의 헤더 선택
        const columnIndex = this.hotInstance.getSettings().columns.findIndex(col => col.data === column.key);
        if (columnIndex !== -1) {
          this.hotInstance.selectColumns(columnIndex);
        }
        this.hotInstance.render();
      },
      show() {
        this.visible = true;
        this.$nextTick(() => {
          if (this.hotInstance) this.hotInstance.render();
          if (this.idListHotInstance) this.idListHotInstance.render();
        });
      },
      close() {
        this.setVisualizationPopObj({openFlag: false, dataObj: {}});
      },
      toggleLeftPanel() {
        this.leftPanelExpanded = !this.leftPanelExpanded;
        this.$nextTick(() => { // 패널 크기 변경 후 테이블 리렌더링
          if (this.idListHotInstance) this.idListHotInstance.render();
        });
      },
      openChartPopup(column, option) {
        if (!option) {
          alert("분석 옵션을 선택해주세요.");
          return;
        }
        const results = this.currentAnalysisResults[column.key];
        if (!results || results.length === 0) {
          alert('차트로 표시할 데이터가 없습니다.');
          return;
        }
        const chartData = this.prepareChartData(results, option.value);
        this.chartPopup = {
          visible: true,
          title: `${column.label} - ${option.text}`,
          chartData: chartData,
          type: option.chartType || 'bar',
        };
      },
      closeChartPopup() {
        this.chartPopup.visible = false;
      },
      scrollToAnalysisColumn(columnKey) {
        this.$nextTick(() => {
          const container = this.$refs.analysisScrollContainer;
          if (!container) return;
          const cards = container.querySelectorAll('.v-popup-analysis-card');
          const targetIndex = this.analysisColumns.findIndex(col => col.key === columnKey);
          if (targetIndex !== -1 && cards[targetIndex]) {
            const target = cards[targetIndex];
            const scrollLeft = target.offsetLeft - container.offsetLeft - 20;
            container.scrollTo({left: scrollLeft, behavior: 'smooth'});
          }
        });
      },
      refreshIdList() {
        // 검색어 초기화
        this.searchKeyword = '';

        // 현재 적용된 ID만 체크되도록 설정
        const refreshedData = this.originalIdData.map(row => ({
          ...row,
          selected: this.appliedIds.includes(row[this.idListHotSettings.columns[1].data])
        }));
        this.originalIdData = refreshedData;
        this.idListHotInstance.loadData(refreshedData);
        this.updateSelectedIdsFromTable();
        this.idListHotInstance.scrollViewportTo(0, 0);
      },
      /*######################################################################################*/
      /*통계*/
      calculateFrequency(data, key) {
        // 빈도수 분석
        const total = data.length;
        if (total === 0) return [];
        const counts = data.reduce((acc, item) => {
          const value = item[key] || '빈 값';
          acc[value] = (acc[value] || 0) + 1;
          return acc;
        }, {});
        return Object.entries(counts)
            .sort(([, a], [, b]) => b - a)
            .map(([value, count]) => `${value}: ${count}건 (${((count / total) * 100).toFixed(0)}%)`);
      },
      calculateStatistical(data, key) {
        // 통계적 비교
        const values = data.map(item => parseFloat(item[key])).filter(v => !isNaN(v));
        if (values.length < 2) return ['데이터 부족'];
        const sum = values.reduce((acc, val) => acc + val, 0);
        const mean = sum / values.length;
        const sorted = [...values].sort((a, b) => a - b);
        const median = sorted.length % 2 === 0
            ? (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2
            : sorted[Math.floor(sorted.length / 2)];
        return [`평균: ${mean.toFixed(2)}`, `중앙값: ${median.toFixed(2)}`, `최대: ${Math.max(...values)}`, `최소: ${Math.min(...values)}`];
      },
      calculateRange(data, key) {
        // 범위 분석
        const values = data.map(item => parseFloat(item[key])).filter(v => !isNaN(v));
        if (values.length === 0) return ['데이터 부족'];
        const min = Math.min(...values);
        const max = Math.max(...values);
        const range = max - min;

        // 구간별 분포 계산
        const intervals = 5; // 5개 구간으로 나누기
        const intervalSize = range / intervals;
        const distribution = {};
        for (let i = 0; i < intervals; i++) {
          const start = min + (i * intervalSize);
          const end = min + ((i + 1) * intervalSize);
          const label = `${start.toFixed(1)} ~ ${end.toFixed(1)}`;
          distribution[label] = 0;
        }
        values.forEach(value => {
          const intervalIndex = Math.min(Math.floor((value - min) / intervalSize), intervals - 1);
          const start = min + (intervalIndex * intervalSize);
          const end = min + ((intervalIndex + 1) * intervalSize);
          const label = `${start.toFixed(1)} ~ ${end.toFixed(1)}`;
          distribution[label]++;
        });
        const total = values.length;
        return Object.entries(distribution)
            .sort(([, a], [, b]) => b - a)
            .filter(([, count]) => count > 0)
            .map(([range, count]) => `${range}: ${count}건 (${((count / total) * 100).toFixed(1)}%)`);
      },
      hideChartPreview() {
        this.chartPreview = {
          visible: false, columnKey: null, optionValue: null,
          position: {top: 0, left: 0},
          data: {labels: [], datasets: []},
        }
      },
      prepareChartData(analysisResults, analysisType) {
        const labels = [];
        const data = [];
        const backgroundColors = [
          'rgba(255, 99, 132, 0.5)', 'rgba(54, 162, 235, 0.5)',
          'rgba(255, 206, 86, 0.5)', 'rgba(75, 192, 192, 0.5)',
          'rgba(153, 102, 255, 0.5)', 'rgba(255, 159, 64, 0.5)',
        ];
        analysisResults.forEach(result => {
          let match;
          if (analysisType === 'frequency' || analysisType === 'range') {
            match = result.match(/^(.*):\s*(\d+)/);
          } else if (analysisType === 'statistical') {
            match = result.match(/^(.*?):\s*([0-9.]+)/);
          }
          if (match && match[1] && match[2]) {
            labels.push(match[1].trim());
            data.push(parseFloat(match[2]));
          }
        });
        return {
          labels,
          datasets: [{
            label: '분석 결과',
            data,
            backgroundColor: data.map((_, i) => backgroundColors[i % backgroundColors.length])
          }],
        };
      },
      showChartPreview(event, column, option) {
        const results = this.currentAnalysisResults[column.key];
        if (!results || results.length === 0) return;
        const chartData = this.prepareChartData(results, option.value);
        const rect = event.currentTarget.getBoundingClientRect();
        this.chartPreview = {
          visible: true,
          columnKey: column.key,
          optionValue: option.value,
          position: {
            top: rect.bottom + window.scrollY + 5, // 버튼 바로 아래에 표시
            left: rect.left + window.scrollX,      // 버튼 왼쪽에 맞춤
          },
          data: chartData,
        };
      },
      /*######################################################################################*/
      /*기타*/
      resetState() {
        this.visible = false;
        this.selectedIds = [];
        this.appliedIds = [];
        this.selectedColumnKey = null;
        this.cellsFromDB = [];
        this.originalIdData = [];
        this.originalTableData = [];
        this.analysisColumns = [];
        this.searchKeyword = '';
        if (this.hotInstance) {
          this.hotInstance.loadData([]);
          this.hotInstance.updateSettings({
            colHeaders: false
          });
        }
        if (this.idListHotInstance) {
          this.idListHotInstance.loadData([]);
        }
      },
      getKoreanTimeString() {
        const now = new Date();
        // 한국 시간으로 변환
        const koreaTime = new Date(now.toLocaleString("en-US", { timeZone: "Asia/Seoul" }));
    
        const yyyy = koreaTime.getFullYear();
        const mm = String(koreaTime.getMonth() + 1).padStart(2, "0");
        const dd = String(koreaTime.getDate()).padStart(2, "0");
        const hh = String(koreaTime.getHours()).padStart(2, "0");
        const min = String(koreaTime.getMinutes()).padStart(2, "0");
        const ss = String(koreaTime.getSeconds()).padStart(2, "0");
    
        return `${yyyy}-${mm}-${dd}_${hh}${min}${ss}`;
      },
      removeHtmlTags(str) {
        return str.replace(/<[^>]*>/g, '');
      },
      /*######################################################################################*/
      /*엑셀*/
      async downloadExcel() {
        if (!this.hotInstance) {
          alert('다운로드할 데이터가 없습니다.');
          return;
        }
        
        // 현재 계산된 통계 결과 준비
        const analysisResults = [];
        this.analysisColumns.forEach(column => {
          const results = this.currentAnalysisResults[column.key];
          if (results && results.length > 0) {
            const analysisType = this.columnAnalysisSelections[column.key];
            const analysisOption = column.analysisOptions.find(o => o.value === analysisType);
            const chartData = this.prepareChartData(results, analysisType);
            
            analysisResults.push({
              columnName: column.label,
              analysisType: analysisOption.text,
              chartData: {
                labels: chartData.labels,
                values: chartData.datasets[0].data
              },
              rawResults: results
            });
          }
        });
      
        // Handsontable 설정에서 컬럼 정보 추출
        const columns = this.hotInstance.getSettings().columns || [];
        const visibleColumns = columns.filter((col, index) => !this.hotInstance.getPlugin('hiddenColumns').isHidden(index));
      
        const requestData = {
          processId: this.$route.query.processId,
          selectedIds: this.appliedIds,
          selectedData: this.appliedIds.length > 0 
            ? this.originalTableData.filter(row => this.appliedIds.includes(row[this.idColumnKey]))
            : this.originalTableData,
          // headers: 표시될 헤더 이름 목록
          headers: visibleColumns.map(col => col.propertyUnit != null && col.propertyUnit !== "" ? `${col.name}[${col.propertyUnit}]` : `${col.name}`),
          // dataKeys: selectedData의 Map에서 값을 찾기 위한 실제 키 목록
          dataKeys: visibleColumns.map(col => col.data), 
          analysisResults: analysisResults,
          selectedNodeInfo: this.getVisualizationPopObj.selectedNodeInfo
        };
        
        this.loading = true;
        try {
          const response = await this.$wApi.experiment.downloadVisualizationExcel(requestData, null, {responseType: 'blob'});
          const nodeOrder = this.getVisualizationPopObj?.selectedNodeInfo?.nodeOrder || null;
          const nodeText = this.getVisualizationPopObj?.selectedNodeInfo?.text || '';
          let fileName = nodeText 
            ? `${nodeText}${nodeOrder ? '_' + nodeOrder : ""}_데이터분석_${this.getKoreanTimeString()}.xlsx`
            : `데이터분석_${this.getKoreanTimeString()}.xlsx`;
          
          const processName = this.getSelectedReleaseProcess.name || '';
          fileName = processName ? `${this.removeHtmlTags(processName)}_${fileName}` : `${fileName}`  

          const blob = new Blob([response.data], { 
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
          });
          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = fileName;
          link.click();
          URL.revokeObjectURL(link.href);
          
        } catch (error) {
          console.error('Excel 다운로드 중 오류 발생:', error);
          alert('Excel 파일을 생성하는 중 오류가 발생했습니다.');
        } finally {
          this.loading = false;
        }
      },
    },
  }
</script>

<style lang="scss">
  @import "@/style/new/css/cyl.common.css";
  @import 'handsontable/dist/handsontable.full.css';

  /* Handsontable 하이라이트 스타일 */
  .handsontable .htCore td.is-highlighted {
    background-color: #e3f2fd !important;
  }

  .handsontable .htCore td.is-col-highlighted {
    background-color: rgba(0, 123, 255, 0.08) !important;
  }

  /* 고정 컬럼 스타일 */
  .handsontable .htCore .ht_clone_left {
    background-color: #f8f9fa;
    border-right: 2px solid #dee2e6;
  }

  .handsontable .htCore .ht_clone_left td {
    background-color: #f8f9fa !important;
    font-weight: 600;
  }

  /* 왼쪽 ID 리스트 Handsontable 스타일 오버라이드 */
  .v-popup-left-panel__list .handsontable .htCore {
    td, th {
      border: none;
    }

    td.htCenter {
      text-align: center;
    }
  }

  .v-popup-left-panel__list .handsontable .htCore tr:hover td {
    background-color: #e9e9e9;
  }

  // 변수
  $primary-color: #007bff;
  $border-color: #ddd;
  $header-bg-color: #f5f5f5;

  // 기본 팝업 레이아웃
  .v-popup-formation-cycle {
    display: flex;
    flex-direction: column;
    position: fixed;
    width: 1200px;
    height: 85%;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    z-index: 999;
    background: #fff;
    box-shadow: 0 5px 20px rgba(0, 0, 0, 0.2);
  }

  .v-popup-mask {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.4);
    z-index: 998;
  }

  .v-popup__header {
    padding: 12px 15px;
    border-bottom: 1px solid $border-color;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 14px;
    font-weight: 600;
  }

  .v-popup__content {
    flex: 1;
    overflow: hidden;
  }

  .v-popup__footer {
    padding: 10px 15px;
    border-top: 1px solid $border-color;
    text-align: right;
    background: $header-bg-color;

    a {
      margin-left: 8px;
    }
  }

  .v-popup__close-btn {
    width: 24px;
    height: 24px;
    cursor: pointer;
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;

    .v-popup__close-box {
      width: 100%;
      height: 100%;
      position: relative;

      span {
        position: absolute;
        width: 14px;
        height: 2px;
        background-color: #666;
        top: 50%;
        left: 50%;
        transform-origin: center;
        transition: background-color 0.2s;

        &:first-child {
          transform: translate(-50%, -50%) rotate(45deg);
        }

        &:last-child {
          transform: translate(-50%, -50%) rotate(-45deg);
        }
      }
    }

    &:hover .v-popup__close-box span {
      background-color: #333;
    }
  }

  .v-popup__input {
    width: 100%;
    padding: 6px 8px;
    border: 1px solid #ccc;
    font-size: 11px;
    border-radius: 3px;
    box-sizing: border-box;
  }

  .v-popup__checkbox-label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    cursor: pointer;
  }

  .v-popup__radio-label {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 10px;
    cursor: pointer;
    margin-bottom: 4px;
  }

  // 레이아웃
  .v-popup-layout {
    display: flex;
    height: 100%;
  }

  // 왼쪽 패널
  .v-popup-layout__left-panel {
    border-right: 1px solid $border-color;
    display: flex;
    flex-direction: column;
    background: #f9f9f9;
    transition: width 0.3s ease;
  }

  .v-popup-accordion-header {
    padding: 12px;
    background: $header-bg-color;
    font-size: 12px;
    font-weight: 600;
    text-align: center;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .is-collapsed & {
      writing-mode: vertical-rl;
      padding: 20px 12px;
    }
  }

  .v-popup-accordion__arrow {
    transition: transform 0.15s;
  }

  .v-popup-left-panel__content-wrapper {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .v-popup-left-panel__search {
    padding: 8px 12px;
    border-bottom: 1px solid $border-color;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .v-popup-left-panel__list {
    flex: 1;
    overflow: hidden;
  }

  .v-popup-left-panel__footer {
    padding: 8px 12px;
    border-top: 1px solid $border-color;
    background: $header-bg-color;
  }

  // 오른쪽 패널
  .v-popup-layout__right-panel {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .v-popup-right-panel__top {
    height: 250px;
    border-bottom: 1px solid $border-color;
    background: #fafafa;
    padding: 12px;
    box-sizing: border-box;
  }

  .v-popup-analysis-scroll-container {
    width: 100%;
    height: 100%;
    overflow-x: auto;
    overflow-y: hidden;
  }

  .v-popup-analysis-container {
    display: flex;
    gap: 16px;
    height: 100%;
    padding-bottom: 10px;
  }

  .v-popup-analysis-card {
    min-width: 220px;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    background: #fff;
    padding: 12px;
    display: flex;
    flex-direction: column;
    cursor: pointer;
    transition: all 0.2s;

    &.is-highlighted {
      border-color: $primary-color;
      box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
    }

    &:hover {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }
  }

  .v-popup-analysis-card__title {
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 10px;
    white-space: nowrap;
    text-overflow: ellipsis;
    padding-bottom: 6px;
    border-bottom: 1px solid #f0f0f0;
    line-height: 1.4;
  }

  .v-popup-analysis-card__options {
    margin-bottom: 12px;
  }

  .v-popup-analysis-controls {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 10px;
  }

  .v-popup-radio-group {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
  }

  .v-popup-chart-btn {
    font-size: 10px;
    color: $primary-color;
    cursor: pointer;
    padding: 4px 8px;
    border: 1px solid $primary-color;
    border-radius: 3px;
    background: transparent;
    transition: all 0.2s;
    white-space: nowrap;

    &:hover {
      color: #fff;
      background: $primary-color;
    }
  }

  .v-popup-analysis-card__results {
    flex: 1;
    overflow-y: auto;
    font-size: 11px;
    border: 1px solid #f0f0f0;
    background: #f9f9f9;
    padding: 10px;
    border-radius: 3px;
    min-height: 80px;
    max-height: 120px;
  }

  .v-popup-result-item {
    font-size: 10px;
    padding: 4px 6px;
    margin-bottom: 3px;
    border-left: 3px solid;
    background: #fff;
    border-radius: 2px;

    &.is-style-frequency { border-left-color: #28a745; }
    &.is-style-statistical { border-left-color: #ffc107; }
    &.is-style-pattern { border-left-color: #17a2b8; }
    &.is-style-range { border-left-color: #dc3545; }
  }

  .v-popup-right-panel__bottom {
    flex: 1;
    overflow: hidden;
  }

  .v-popup-chart-preview {
    position: fixed;
    z-index: 1000;
    background: #fff;
    border: 1px solid #ccc;
    border-radius: 4px;
    padding: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    pointer-events: none;
  }

  .v-popup-refresh-btn {
    background: none;
    border: 1px solid #ccc;
    border-radius: 3px;
    padding: 4px 6px;
    cursor: pointer;
    font-size: 12px;
    transition: all 0.2s;
    margin-left: auto;

    &:hover {
      background-color: #f0f0f0;
      border-color: $primary-color;
    }
  }

  @media (max-width: 1400px) {
    .v-popup-formation-cycle { width: 90%; }
  }
  @media (max-width: 1024px) {
    .v-popup-formation-cycle { width: 95%; height: 90%; }
    .v-popup-layout__left-panel { width: 200px; }
    .v-popup-analysis-card { min-width: 180px; }
  }
  @media (max-width: 768px) {
    .v-popup-formation-cycle { width: 100%; height: 100vh; top: 0; left: 0; transform: none; }
    .v-popup__content { overflow-y: auto; -webkit-overflow-scrolling: touch; }
    .v-popup-layout { flex-direction: column; height: auto; min-height: 100%; }
    .v-popup-layout__left-panel { width: 100%; height: 250px; border-right: none; border-bottom: 1px solid $border-color; flex-shrink: 0; }
    .v-popup-right-panel__top { height: 200px; flex-shrink: 0; }
    .v-popup-right-panel__bottom { flex: 1; min-height: 300px; }
  }
</style>